<template>
    <div class="dialog">
        <v-dialog
            v-model="show"
            max-width="80vw"
            persistent
            scrollable
        >
            <v-card>
                <CardTitle :type="type" />

                <v-card-text>
                    <v-container>
                        <v-form
                            ref="form"
                            v-model="valid"
                        >
                            <div
                                v-if="type === '修改'"
                                class="title-box"
                            >
                                <span class="title">帳號：{{ editData.account }}</span>
                            </div>
                            <div class="title-box">
                                <span class="title">個人資料</span>
                            </div>
                            <v-row class="text-box">
                                <v-col cols="4">
                                    <v-text-field
                                        v-model="editData.name"
                                        label="*姓名"
                                        outlined
                                        persistent-placeholder
                                        clearable
                                        :rules="emptyRule"
                                    />
                                </v-col>
                                <v-col
                                    v-if="type === '新增'"
                                    cols="4"
                                >
                                    <v-text-field
                                        v-model="editData.account"
                                        label="*帳號"
                                        outlined
                                        persistent-placeholder
                                        clearable
                                        :rules="emptyRule"
                                    />
                                </v-col>
                                <v-col
                                    v-if="type === '新增'"
                                    cols="4"
                                    style="position: relative"
                                >
                                    <v-text-field
                                        v-model="editData.password"
                                        label="*密碼"
                                        outlined
                                        :type="showPWD ? 'text' : 'password'"
                                        :append-icon="showPWD ? 'mdi-eye' : 'mdi-eye-off'"
                                        persistent-placeholder
                                        clearable
                                        :rules="passwordRule"
                                        @click:append="showPWD = !showPWD"
                                        @input="checkPasswordStrength"
                                    />
                                    <div class="password-requirements">
                                        <div :class="['requirement', { met: hasMinLength }]">
                                            八字以上
                                        </div>
                                        <div class="divider" />
                                        <div :class="['requirement', { met: hasUpperCase }]">
                                            英文大寫
                                        </div>
                                        <div class="divider" />
                                        <div :class="['requirement', { met: hasLowerCase }]">
                                            英文小寫
                                        </div>
                                        <div class="divider" />
                                        <div :class="['requirement', { met: hasNumber }]">
                                            數字
                                        </div>
                                        <div class="divider" />
                                        <div :class="['requirement', { met: hasSpecial }]">
                                            特殊字元
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                            <div v-if="title === 'invention'">
                                <div class="title-box">
                                    <span class="title">大專校院組</span>
                                </div>
                                <v-row class="text-box">
                                    <v-col cols="6">
                                        <v-combobox
                                            v-model="editData.group.universities"
                                            :items="inventionItems"
                                            label="內容"
                                            chips
                                            outlined
                                            multiple
                                        />
                                    </v-col>
                                </v-row>
                                <div class="title-box">
                                    <span class="title">高中職組</span>
                                </div>
                                <v-row class="text-box">
                                    <v-col cols="6">
                                        <v-combobox
                                            v-model="editData.group.senior"
                                            :items="inventionItems"
                                            label="內容"
                                            chips
                                            outlined
                                            multiple
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-if="title === 'incubator'">
                                <div class="title-box">
                                    <span class="title">孵化組</span>
                                </div>
                                <v-row class="text-box">
                                    <v-col cols="6">
                                        <v-combobox
                                            v-model="editData.group.incubation"
                                            :items="incubatorItems"
                                            label="內容"
                                            chips
                                            outlined
                                            multiple
                                        />
                                    </v-col>
                                </v-row>
                                <div class="title-box">
                                    <span class="title">加速組</span>
                                </div>
                                <v-row class="text-box">
                                    <v-col cols="6">
                                        <v-combobox
                                            v-model="editData.group.acceleration"
                                            :items="incubatorItems"
                                            label="內容"
                                            chips
                                            outlined
                                            multiple
                                        />
                                    </v-col>
                                </v-row>
                                <!-- 本屆無亞太地區團隊，暫時隱藏；恢復時將 showAsiaPacific 改回 true -->
                                <template v-if="showAsiaPacific">
                                    <div class="title-box">
                                        <span class="title">亞太地區</span>
                                    </div>
                                    <v-row class="text-box">
                                        <v-col cols="6">
                                            <v-combobox
                                                v-model="editData.group.asia"
                                                :items="incubatorItems"
                                                label="內容"
                                                chips
                                                outlined
                                                multiple
                                            />
                                        </v-col>
                                    </v-row>
                                </template>
                            </div>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn
                        color="blue darken-1"
                        text
                        @click="closeEdit"
                    >
                        取消
                    </v-btn>
                    <v-btn
                        color="blue darken-1"
                        text
                        @click="submitEdit"
                    >
                        儲存
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import CardTitle from '@/components/UI/CardTitle.vue';
import dialogMixin from '@/mixins/dialogMixin.vue';

export default {
    components: { CardTitle },
    mixins: [dialogMixin],
    props: {
        title: {
            type: String,
            default: 'invention',
        },
    },
    data() {
        return {
            showPWD: false,
            valid: false,
            emptyRule: [(v) => !!v || '此欄位為必填'],
            passwordRule: [
                (v) => !!v || '此欄位為必填',
                (v) => this.validatePassword(v) || '不符合規則',
                (v) => !v.match(/\s/) || '輸入不可包含空格',
            ],
            editData: {
                account: '',
                password: '',
                name: '',
                group: {
                    universities: '',
                    senior: '',
                    incubation: '',
                    acceleration: '',
                    asia: '',
                },
            },
            type: '',
            // 本屆無亞太地區團隊，評審指派暫時隱藏；恢復時改為 true
            showAsiaPacific: false,
            incubatorItems: [
                '慈善共好',
                '健康福祉',
                '教育創新',
                '韌性城鄉',
                '循環經濟',
            ],
            inventionItems: ['慈善', '醫療'],
            hasMinLength: false,
            hasUpperCase: false,
            hasLowerCase: false,
            hasNumber: false,
            hasSpecial: false,
        };
    },
    computed: {},
    created() {},
    mounted() {},
    methods: {
        validatePassword(password) {
            if (!password) return false;

            const minLength = password.length >= 8;
            const hasUpper = /[A-Z]/.test(password);
            const hasLower = /[a-z]/.test(password);
            const hasNum = /[0-9]/.test(password);
            const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

            return minLength && hasUpper && hasLower && hasNum && hasSpecial;
        },
        checkPasswordStrength(password) {
            this.hasMinLength = password.length >= 8;
            this.hasUpperCase = /[A-Z]/.test(password);
            this.hasLowerCase = /[a-z]/.test(password);
            this.hasNumber = /[0-9]/.test(password);
            this.hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        },
        editItem(data) {
            if (data) {
                this.type = '修改';
                this.editData = {
                    id: data.id,
                    account: data.account,
                    name: data.data.name,
                    group: {
                        universities: data.data.groupIssues['大專校院組'],
                        senior: data.data.groupIssues['高中職組'],
                        incubation: data.data.groupIssues['臺灣團隊-孵化組'],
                        acceleration: data.data.groupIssues['臺灣團隊-加速組'],
                        asia: data.data.groupIssues['亞太地區團隊'],
                    },
                };
            } else {
                this.type = '新增';
                this.editData = {
                    account: '',
                    password: '',
                    name: '',
                    group: {
                        universities: '',
                        senior: '',
                        incubation: '',
                        acceleration: '',
                        asia: '',
                    },
                };
            }
        },
        closeEdit() {
            this.show = false;
            this.$emit('search');
        },
        submitEdit() {
            if (this.$refs.form.validate()) {
                this.$emit('saveData', this.editData);
                this.show = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.password-requirements {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 12px;
    position: absolute;
    bottom: 18px;
    right: 0;
    .divider {
        width: 1px;
        height: auto;
        background-color: #cccccc;
    }

    .requirement {
        font-size: 12px;
        color: #cccccc;
        font-weight: 700;
        transition: color 0.3s ease;

        &.met {
            color: #4caf50;
        }
    }
}
</style>
